import { useCallback, useEffect, useMemo, useRef } from "react";
import { useSearchParams } from "react-router-dom";

/**
 * Bộ lọc của một trang danh sách nằm TRÊN URL — một nguồn sự thật duy nhất.
 *
 * Trước đây mỗi trang giữ bộ lọc trong 4–6 `useState`, rồi (một vài trang) chép tay lên URL bằng
 * một `useEffect` riêng; phần lớn trang còn lại mất bộ lọc khi reload hoặc khi quay về từ trang
 * chi tiết. Hook này đảo chiều: URL là state, trang chỉ đọc/ghi qua nó. Hệ quả tự nhiên:
 *
 * - reload / Back / gửi link cho người khác → cùng một danh sách;
 * - một ô KPI hay một link chéo chỉ cần dựng `?status=…&from=…` là mở đúng tập;
 * - đổi bất kỳ điều kiện nào thì `page` tự về đầu (trang cũ không còn nghĩa với tập mới).
 *
 * Quy ước ghi: giá trị **bằng mặc định thì KHÔNG xuất hiện** trên URL (`?` sạch khi chưa lọc gì);
 * giá trị lạ trên URL (trạng thái không tồn tại, ngày sai định dạng) bị coi như vắng mặt. Mọi
 * tham số không thuộc bộ lọc (`highlight`, `metric`…) được giữ nguyên. Ghi bằng `replace` để nút
 * Back đưa về trang trước, không lùi từng lần gõ.
 */
export interface ParamCodec<T> {
  /** Giá trị khi URL không có tham số, hoặc tham số không hợp lệ. */
  fallback: T;
  /** `undefined` = chuỗi không hợp lệ → dùng `fallback`. */
  decode(raw: string): T | undefined;
  /** Chuỗi ghi lên URL. Không được gọi với `fallback` (giá trị đó bị xoá khỏi URL). */
  encode(value: T): string;
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Một trong các giá trị cho trước (trạng thái, phương thức…). Không truyền `fallback` thì `""`
 * nghĩa là "tất cả"; truyền thì tham số vắng mặt đọc thành giá trị đó.
 */
function oneOf<T extends string>(values: readonly T[]): ParamCodec<T | "">;
function oneOf<T extends string>(values: readonly T[], fallback: T): ParamCodec<T>;
function oneOf<T extends string>(values: readonly T[], fallback?: T): ParamCodec<T | ""> {
  return {
    fallback: fallback ?? "",
    decode: (raw) => (values.includes(raw as T) ? (raw as T) : undefined),
    encode: (v) => v,
  };
}

/** Bộ codec cho các kiểu tham số hay gặp trên trang danh sách. */
export const param = {
  /** Chuỗi tự do (ô tìm kiếm, id bác sĩ…). `""` = không lọc. */
  string(fallback = ""): ParamCodec<string> {
    return { fallback, decode: (raw) => raw, encode: (v) => v };
  },

  oneOf,

  /** Ngày `YYYY-MM-DD`. `fallback` có thể là hàm (vd. hôm nay) để tính tại thời điểm đọc. */
  isoDate(fallback: string | (() => string) = ""): ParamCodec<string> {
    return {
      get fallback() {
        return typeof fallback === "function" ? fallback() : fallback;
      },
      decode: (raw) => (ISO_DATE.test(raw) ? raw : undefined),
      encode: (v) => v,
    };
  },

  /** Số nguyên ≥ `min` (số trang, giới hạn dòng). */
  int(fallback = 0, min = 0): ParamCodec<number> {
    return {
      fallback,
      decode: (raw) => {
        const n = Number(raw);
        return Number.isInteger(n) && n >= min ? n : undefined;
      },
      encode: (v) => String(v),
    };
  },

  /** Cờ bật/tắt, ghi là `1`. */
  bool(fallback = false): ParamCodec<boolean> {
    return {
      fallback,
      decode: (raw) => (raw === "1" ? true : raw === "0" ? false : undefined),
      encode: (v) => (v ? "1" : "0"),
    };
  },
};

type CodecSpec = Record<string, ParamCodec<unknown>>;

export type FilterValues<S extends CodecSpec> = {
  [K in keyof S]: S[K] extends ParamCodec<infer T> ? T : never;
};

export interface UseUrlFiltersOptions<S extends CodecSpec> {
  /**
   * Khoá về mặc định mỗi khi một khoá KHÁC đổi — mặc định là `"page"` nếu bộ lọc có khoá đó.
   * `false` để tắt.
   */
  resetOnChange?: keyof S | false;
}

export interface UrlFilters<S extends CodecSpec> {
  /** Giá trị hiện tại, đã qua `decode` + `fallback`. */
  filters: FilterValues<S>;
  /**
   * Ghi một hoặc nhiều khoá. Các khoá không nêu giữ nguyên; `resetOnChange` (thường là `page`) về
   * mặc định trừ khi chính nó nằm trong `patch`. Gọi nhiều lần trong cùng một sự kiện vẫn cộng dồn.
   */
  set(patch: Partial<FilterValues<S>>): void;
  /** Về mặc định: toàn bộ khoá của bộ lọc, hoặc chỉ các khoá nêu tên. */
  reset(keys?: (keyof S)[]): void;
  /** Khoá nào đang khác mặc định. */
  isDefault(key: keyof S): boolean;
}

export function useUrlFilters<S extends CodecSpec>(
  spec: S,
  options: UseUrlFiltersOptions<S> = {},
): UrlFilters<S> {
  const [searchParams, setSearchParams] = useSearchParams();
  const resetKey: keyof S | false =
    options.resetOnChange === undefined ? ("page" in spec ? "page" : false) : options.resetOnChange;

  // `spec` thường là object literal viết ngay trong component → identity đổi mỗi lần vẽ. Giữ bản
  // mới nhất trong ref để các callback dưới không phải phụ thuộc vào nó.
  const specRef = useRef(spec);
  specRef.current = spec;

  const search = searchParams.toString();
  const filters = useMemo(
    () => readValues(spec, new URLSearchParams(search)) as FilterValues<S>,
    // Chỉ chuỗi URL mới làm đổi kết quả; `spec` đổi identity mỗi lần vẽ nhưng không đổi nghĩa.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [search],
  );

  // Hai lần `set` trong cùng một sự kiện: lần hai phải dựa trên lần một chứ không phải trên
  // `searchParams` của lần vẽ trước (react-router chưa vẽ lại giữa hai lần gọi).
  const pending = useRef<URLSearchParams | null>(null);
  useEffect(() => {
    pending.current = null;
  });

  const write = useCallback(
    (mutate: (next: URLSearchParams, current: FilterValues<S>) => void) => {
      const base = pending.current ?? searchParams;
      const next = new URLSearchParams(base);
      const current = readValues(specRef.current, base) as FilterValues<S>;
      mutate(next, current);
      if (next.toString() === base.toString()) return;
      pending.current = next;
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const set = useCallback(
    (patch: Partial<FilterValues<S>>) => {
      write((next, current) => {
        const s = specRef.current;
        const touched: Record<string, unknown> = { ...patch };
        if (resetKey !== false && !(resetKey in patch)) {
          const changed = Object.keys(patch).some((k) => k !== resetKey && patch[k] !== current[k]);
          if (changed) touched[resetKey as string] = s[resetKey as string].fallback;
        }
        // Chỉ chạm vào khoá có trong `patch`: khoá chưa đổi giữ nguyên CHUỖI trên URL (kể cả khi
        // chuỗi đó không qua được `decode` — vd. một trang khác dùng chung tên khoá với nghĩa
        // khác), và vị trí của khoá đang sửa không nhảy ra cuối.
        for (const key of Object.keys(touched)) {
          const codec = s[key];
          const value = touched[key];
          if (value === codec.fallback) next.delete(key);
          else next.set(key, codec.encode(value));
        }
      });
    },
    [write, resetKey],
  );

  const reset = useCallback(
    (keys?: (keyof S)[]) => {
      write((next) => {
        const s = specRef.current;
        for (const key of keys ?? Object.keys(s)) next.delete(key as string);
        if (resetKey !== false) next.delete(resetKey as string);
      });
    },
    [write, resetKey],
  );

  const isDefault = (key: keyof S) => filters[key] === spec[key as string].fallback;

  return { filters, set, reset, isDefault };
}

function readValues(spec: CodecSpec, params: URLSearchParams): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const key of Object.keys(spec)) {
    const raw = params.get(key);
    const decoded = raw === null ? undefined : spec[key].decode(raw);
    out[key] = decoded === undefined ? spec[key].fallback : decoded;
  }
  return out;
}
